/**
 * ========================
 * BASE MENU PROVIDER UTILITIES
 * ========================
 *
 * Shared utilities for implementing domain menu providers.
 *
 * Provides:
 * - Visibility check creation from legacy roles/permissions
 * - User context extraction from auth state
 * - Helper functions for common provider patterns
 */

import { MenuItemVisibility } from '../../layouts/menu/types';
import { hasAccess, AccessRule } from '../../components/security/AccessControl';
import { useAuth } from '@actschurch/ccms-sdk';

/**
 * ========================
 * VISIBILITY HELPERS
 * ========================
 */

/**
 * Create a visibility check function from legacy AccessRule.
 *
 * Converts the declarative AccessControl format into a functional
 * visibility check compatible with the new menu system.
 *
 * USAGE:
 * ```typescript
 * const item: MenuItem = {
 *   id: 'admin-panel',
 *   label: 'Admin Panel',
 *   path: '/admin',
 *   visible: createVisibilityCheck({ roles: ['Admin'] }),
 * };
 * ```
 *
 * @param rule - Optional access rule with roles, permissions, domains
 * @returns Visibility function that checks user context
 */
export function createVisibilityCheck(rule?: AccessRule): MenuItemVisibility {
  // If no rule, always visible
  if (!rule || (!rule.roles && !rule.permissions && !rule.domains)) {
    return () => true;
  }

  // Return closure that captures rule and checks against user context
  return (context) => {
    return hasAccess(
      context.userRoles,
      context.userPermissions,
      context.userDomains || [],
      rule
    );
  };
}

/**
 * Create visibility check that requires ALL specified roles/permissions.
 *
 * Convenience wrapper for requireAll: true.
 *
 * @param rule - Access rule
 * @returns Visibility function requiring all conditions
 */
export function createStrictVisibilityCheck(rule: AccessRule): MenuItemVisibility {
  return createVisibilityCheck({ ...rule, requireAll: true });
}

/**
 * Create visibility check that combines multiple rules with AND logic.
 *
 * All rules must pass for item to be visible.
 *
 * @param rules - Array of access rules
 * @returns Visibility function requiring all rules to pass
 */
export function combineVisibilityChecks(...rules: AccessRule[]): MenuItemVisibility {
  const checks = rules.map((rule) => createVisibilityCheck(rule));

  return (context) => {
    return checks.every((check) => check(context));
  };
}

/**
 * Create visibility check that combines multiple rules with OR logic.
 *
 * At least one rule must pass for item to be visible.
 *
 * @param rules - Array of access rules
 * @returns Visibility function requiring any rule to pass
 */
export function anyVisibilityCheck(...rules: AccessRule[]): MenuItemVisibility {
  const checks = rules.map((rule) => createVisibilityCheck(rule));

  return (context) => {
    return checks.some((check) => check(context));
  };
}

/**
 * ========================
 * USER CONTEXT HELPERS
 * ========================
 */

/**
 * Get current user context for visibility checks.
 *
 * Extracts roles, permissions, and domains from auth state.
 *
 * IMPORTANT:
 * This function uses React hooks and can ONLY be called from
 * React components, not from provider classes directly.
 *
 * For use in providers, see getUserContextFromState().
 *
 * @returns User context object
 */
export function useUserContext() {
  const { state } = useAuth();
  const user = state.user;

  return {
    userRoles: user?.roles ?? [],
    userPermissions: user?.rights ?? [],
    userDomains: (user as any)?.domains ?? [],
  };
}

/**
 * Get user context from auth state without hooks.
 *
 * Safe to call from provider classes or non-React contexts.
 *
 * USAGE IN PROVIDERS:
 * ```typescript
 * import { getUserContextFromState } from './BaseMenuProvider';
 *
 * class MyProvider implements DomainMenuProvider {
 *   getMenuConfig() {
 *     const userContext = getUserContextFromState();
 *     // Use userContext for conditional logic
 *   }
 * }
 * ```
 *
 * @returns User context object or null if auth not available
 */
export function getUserContextFromState() {
  try {
    const authState = useAuth.getState?.();
    const user = authState?.user;

    if (!user) {
      return {
        userRoles: [],
        userPermissions: [],
        userDomains: [],
      };
    }

    return {
      userRoles: user.roles ?? [],
      userPermissions: user.rights ?? [],
      userDomains: (user as any).domains ?? [],
    };
  } catch (error) {
    // If auth is not available, return empty context
    console.warn('[BaseMenuProvider] Failed to get user context:', error);
    return {
      userRoles: [],
      userPermissions: [],
      userDomains: [],
    };
  }
}

/**
 * ========================
 * FEATURE FLAG HELPERS
 * ========================
 */

/**
 * Create visibility check based on environment variable.
 *
 * Useful for feature flags and environment-specific menus.
 *
 * @param envVar - Environment variable name
 * @param expectedValue - Expected value (default: 'true')
 * @returns Visibility function checking env var
 */
export function createEnvVisibilityCheck(
  envVar: string,
  expectedValue: string = 'true'
): MenuItemVisibility {
  return () => {
    const value = import.meta.env[envVar];
    return value === expectedValue;
  };
}

/**
 * Create visibility check for development-only features.
 *
 * @returns Visibility function showing only in dev mode
 */
export function createDevOnlyVisibilityCheck(): MenuItemVisibility {
  return () => import.meta.env.DEV === true;
}

/**
 * Create visibility check for production-only features.
 *
 * @returns Visibility function showing only in production
 */
export function createProdOnlyVisibilityCheck(): MenuItemVisibility {
  return () => import.meta.env.PROD === true;
}

/**
 * ========================
 * COMBINATION HELPERS
 * ========================
 */

/**
 * Create visibility check that combines role check with feature flag.
 *
 * Item is visible only if user has role AND feature is enabled.
 *
 * @param rule - Access rule for role/permission check
 * @param envVar - Environment variable name
 * @returns Combined visibility function
 */
export function createFeatureFlaggedVisibility(
  rule: AccessRule,
  envVar: string
): MenuItemVisibility {
  const roleCheck = createVisibilityCheck(rule);
  const featureCheck = createEnvVisibilityCheck(envVar);

  return (context) => {
    return roleCheck(context) && featureCheck(context);
  };
}

/**
 * ========================
 * TYPE EXPORTS
 * ========================
 */

// Re-export types for convenience
export type { AccessRule };
export type { MenuItemVisibility } from '../../layouts/menu/types';
